/**
 * ReportsAppointments — the Appointments reports family (APT-10): the merge of
 * the three-in-one's two tenant-split report designs — HE's health metrics
 * (kept / no-show / rescheduled by category) + the bank's booking funnel —
 * into ONE tenant-neutral tab of GM Reports. Wired to getAppointmentsReport
 * (declared, REQ-089; seed-served in preview — DataSourceStrip).
 *
 * 2026-08-31 — brought onto the shared card grammar (./findings-kit), the same
 * one Campus Pulse and the events console speak. What changed and why:
 *   • Six findings (./appt-findings) replace six aggregates that named no
 *     problem, no threshold and no owner. All derived from the SAME response.
 *   • Every rate carries its method line, including what it is measured
 *     against — a 14% no-show rate is unreadable on its own.
 *   • Staff are named only behind the advisor-roster gate, with the workload
 *     framing on the surface. Whether an appointment is kept is mostly the
 *     student's action; a kept-rate league table would read as a rating.
 *   • Cards scope the tables below them.
 *   • The export is gated and logged.
 *
 * THREE THINGS THIS REPORT STILL CANNOT DO, and none of them is a UI decision
 * (asks filed in changelog/2026-09-01T0030-appointments-report.md):
 *   1. Period-over-period. `getAppointmentsReport` takes a range PRESET, not a
 *      shifted window, so a prior period cannot be requested. The per-service
 *      cards use the location's own rate as their baseline instead, and the
 *      tiles carry no deltas rather than fake ones.
 *   2. Drill-down from a row to the appointments behind it. `listAppointments`
 *      is day-scoped with no service, outcome or range filter. The gated CSV of
 *      the report's own rows stands in until that lands.
 *   3. A reminder verb. `sendAppointmentReminder` is per-appointment and needs
 *      ids this response does not carry; there is no service-level reminder
 *      policy op. The cards route to the owner and say what to change.
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, Card, Icon, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { can, isAdmin, useUser, useTenant } from "../../shell/access";
import ConfirmModal from "../../shell/ConfirmModal";
import { toast } from "../../shell/toast";
import { logAction } from "../../lib/audit";
import { useT } from "../../lib/i18n";
import { reportTabs, RangeSelect, LocationSelect, ScoreTile, fmtNum, fmtPct } from "./ReportsOverview";
import { FindingCard, FindingsHeader } from "./findings-kit";
import { apptFindings, MIN_BOOKED, type ApptFinding } from "./appt-findings";
import { getAppointmentsReport, type AppointmentsReport } from "../appointments/appointments-api";

const ORIGIN_LABEL: Record<string, string> = { journey: "Online (journey)", staff: "Staff-booked", concierge: "Concierge", customers: "Customer 360", guests: "Guest follow-up", book: "Day book", import: "Imported" };

const TH = { padding: "7px 18px", fontWeight: 650, fontSize: 11, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: T.soft, textAlign: "left" as const };
const TD = { padding: "9px 18px", fontSize: 12.5, color: T.n2 };
const MONO = { fontFamily: T.fontMono, fontVariantNumeric: "tabular-nums" as const };
const METHOD = { ...MONO, fontSize: 10, color: T.soft, lineHeight: 1.4 };

function Bar({ label, value, max, accent }: { label: string; value: number; max: number; accent: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 48px", gap: 10, alignItems: "center" }}>
      <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ height: 10, background: T.n6, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%`, background: accent, borderRadius: 999, transition: "width 400ms" }} />
      </div>
      <span style={{ ...MONO, fontSize: 12.5, color: T.n1, fontWeight: 700, textAlign: "right" }}>{fmtNum(value)}</span>
    </div>
  );
}

export default function ReportsAppointments() {
  const t = useT();
  const nav = useNav();
  const user = useUser();
  const tenant = useTenant();
  const [range, setRange] = useState("30d");
  const [loc, setLoc] = useState("all");
  const [data, setData] = useState<AppointmentsReport | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [finding, setFinding] = useState<string | null>(null);
  const [bandOpen, setBandOpen] = useState(true);
  const [verbStates, setVerbStates] = useState<Record<string, { state: "done"; n: number; at: number }>>({});
  const [confirmVerb, setConfirmVerb] = useState<ApptFinding | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  /* Staff comparisons are a separate gate from reading the report, exactly as
     in Campus Pulse: without the advisor-roster capability the same spread is
     written without the names rather than redacted after the fact. */
  const staffNamesVisible = isAdmin() || can("advisors.read");
  const canAct = isAdmin() || can("appointments.manage") || can("campaigns.send");
  const canExport = isAdmin() || can("reports.export");

  const load = () => getAppointmentsReport({ range, locationId: loc === "all" ? null : loc })
    .then((r) => { setData(r); setErr(null); }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { load(); }, [range, loc]);
  useEffect(() => { setFinding(null); }, [range, loc]);

  const k = data?.kpis;
  const locName = loc === "all" ? "all locations" : (data?.locations.find((l) => l.id === loc)?.name ?? "this location");
  const rangeLabel = { "7d": "last 7 days", "30d": "last 30 days", "90d": "last 90 days", ytd: "year to date", "12m": "last 12 months" }[range] ?? range;

  const findings = useMemo(() => (data ? apptFindings(data, rangeLabel, staffNamesVisible) : []), [data, rangeLabel, staffNamesVisible]);
  const active = finding ? findings.find((f) => f.id === finding) ?? null : null;
  const scopedIds = active && active.rowsAffected.length ? new Set(active.rowsAffected.map((r) => r.id)) : null;
  const svcRows = (data?.byService ?? []).filter((s) => !scopedIds || active?.kind !== "service" || scopedIds.has(s.id));
  const staffRows = (data?.byStaff ?? []).filter((s) => !scopedIds || active?.kind !== "staff" || scopedIds.has(s.id));
  const funnelMax = Math.max(1, ...(data?.funnel ?? []).map((f) => f.count));
  const svcMax = Math.max(1, ...(data?.byService ?? []).map((s) => s.booked));
  const originMax = Math.max(1, ...(data?.byOrigin ?? []).map((x) => x.count));

  const runVerb = (f: ApptFinding) => {
    if (!f.verb) return;
    logAction("service", `reports-appointments:${f.id}`, `Appointments · ${f.headline}`, f.verb.id, {
      changes: [
        { field: "owner", from: null, to: f.owner },
        { field: "rows", from: null, to: f.rowsAffected.length },
        { field: "window", from: null, to: `${rangeLabel} · ${locName}` },
      ],
    });
    setVerbStates((x) => ({ ...x, [f.id]: { state: "done", n: f.rowsAffected.length, at: Date.now() } }));
    toast({ tone: "success", title: f.verb.done, subtitle: `${f.rowsAffected.length} ${f.rowsAffected.length === 1 ? "service" : "services"} · ${f.owner}` });
  };

  const doExport = () => {
    if (!data || !k) return;
    const esc = (v: unknown) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const when = new Date().toISOString();
    const lines: string[] = [
      `# Appointments report — ${tenant?.name ?? "workspace"}`,
      `# Scope: ${rangeLabel} · ${locName}${active ? ` · finding: ${active.value} ${active.headline}` : ""}`,
      `# Exported by: ${user.name || "unknown"} <${user.email || "—"}> at ${when}`,
      `# Booked ${k.booked} · kept ${k.kept} · no-show ${k.noShow} · cancelled ${k.cancelled} · rescheduled ${k.rescheduled}`,
      "",
      ["Breakdown", "Name", "Booked", "Kept", "No-shows", "Kept %", "No-show %"].join(","),
    ];
    svcRows.forEach((s) => lines.push(["Service", esc(s.name), s.booked, s.kept, s.noShow,
      s.booked ? Math.round((s.kept / s.booked) * 100) : 0, s.booked ? Math.round((s.noShow / s.booked) * 100) : 0].join(",")));
    if (staffNamesVisible) {
      staffRows.forEach((s) => lines.push(["Staff", esc(s.name), s.booked, s.kept, s.noShow,
        s.booked ? Math.round((s.kept / s.booked) * 100) : 0, s.booked ? Math.round((s.noShow / s.booked) * 100) : 0].join(",")));
    }
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const el = document.createElement("a");
    el.href = url; el.download = `appointments-${range}-${when.slice(0, 10)}.csv`;
    document.body.appendChild(el); el.click(); el.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    logAction("service", `reports-appointments-export:${range}`, "Appointments report export", "exported", {
      changes: [
        { field: "rows", from: null, to: svcRows.length + (staffNamesVisible ? staffRows.length : 0) },
        { field: "scope", from: null, to: `${rangeLabel} · ${locName}` },
        { field: "staffNames", from: null, to: staffNamesVisible },
      ],
    });
    toast({ tone: "success", title: "Export logged", subtitle: "Recorded in the audit trail" });
    setExportOpen(false);
  };

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "appointments")} activeTab="appointments">
      <PageHeader
        title={t("reports.appointments.appointments", "Appointments")}
        subtitle={`Booking health — kept, missed, moved and where the bookings come from · ${rangeLabel} · ${locName}`}
        breadcrumb={false}
        actions={<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 10.5, color: T.soft }}>Viewing as <strong style={{ color: T.n1 }}>{user.role || "—"}</strong></span>
          <LocationSelect value={loc} onChange={setLoc} locs={data?.locations ?? []} />
          <RangeSelect value={range} onChange={setRange} />
          {canExport ? <Btn variant="secondary" icon={<Icon.csv size={14} />} onClick={() => setExportOpen(true)}>Export · gated</Btn> : null}
        </div>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        {err ? (
          <Card style={{ padding: "14px 16px", border: `1px solid ${T.red1}`, background: T.red2, color: T.red1, fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>{t("reports.appointments.loadFailed", "Couldn't load the appointments report")} — {err}</span>
            <Btn size="sm" variant="secondary" onClick={load}>{t("appointments.book.retry", "Retry")}</Btn>
          </Card>
        ) : !data || !k ? (
          <div style={{ fontSize: 13, color: T.soft, padding: "40px 0", textAlign: "center" }}>{t("common.status.loading", "Loading…")}</div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <FindingsHeader n={findings.length} open={bandOpen} onToggle={() => setBandOpen(!bandOpen)}
                note={`thresholded against this location’s own rates · services under ${MIN_BOOKED} bookings excluded`} />
              {bandOpen ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, alignItems: "stretch" }}>
                  {findings.map((f) => (
                    <FindingCard key={f.id} f={f} verb={f.verb} filterable={f.rowsAffected.length > 0}
                      active={finding === f.id}
                      onToggle={() => setFinding(finding === f.id ? null : f.id)}
                      filterLabel={f.kind === "staff" ? "Filter staff" : "Filter services"}
                      verbState={verbStates[f.id]} onVerb={() => setConfirmVerb(f)}
                      canAct={canAct}
                      actDenyReason="Your role can read this report but not route appointment work" />
                  ))}
                </div>
              ) : null}
              {active && active.rowsAffected.length ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 14px", background: T.p4, border: `1px solid ${T.p1}`, borderRadius: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.p1, whiteSpace: "nowrap" }}>Scoped to “{active.value} {active.headline}” · {active.rowsAffected.length} {active.kind === "staff" ? "staff" : "services"}</span>
                  <span style={{ fontSize: 11.5, color: T.n2 }}>The {active.kind === "staff" ? "staff" : "service"} table below shows only these rows. The totals stay on the whole window — they are what the finding is measured against.</span>
                  <Btn variant="ghost" onClick={() => setFinding(null)}>Clear</Btn>
                </div>
              ) : null}
              {staffNamesVisible ? (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 14px", background: T.orange2, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0, marginTop: 1 }}><Icon.info size={13} /></span>
                  <span style={{ fontSize: 11.5, lineHeight: 1.5, color: T.n1, textWrap: "pretty" }}>
                    Kept rate per staff member is <strong>workload evidence, not a performance rating</strong>. Whether an appointment is kept is mostly the student’s action and the reminder’s; the figure is not adjusted for time of day, service or booking lead time — the three things most likely to explain a difference. Visibility of staff comparisons is a role question.
                  </span>
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <ScoreTile label={t("reports.appointments.booked", "Booked")} value={fmtNum(k.booked)} accent={T.p1} sub={t("reports.appointments.allChannels", "all channels")} />
                <ScoreTile label={t("reports.appointments.keptRate", "Kept rate")} value={fmtPct(k.keptRate)} accent={T.green1} sub={`${fmtNum(k.kept)} of ${fmtNum(k.booked)} booked`} />
                <ScoreTile label={t("reports.appointments.noShowRate", "No-show rate")} value={fmtPct(k.noShowRate)} accent={T.orange1} sub={`${fmtNum(k.noShow)} of ${fmtNum(k.booked)} booked`} />
                <ScoreTile label={t("reports.appointments.rescheduled", "Rescheduled")} value={fmtNum(k.rescheduled)} accent={T.blue1} sub={`${fmtNum(k.cancelled)} ${t("reports.appointments.cancelled", "cancelled")}`} />
              </div>
              <span style={METHOD}>
                Rates are over the contract’s own “booked” count for {rangeLabel} · {locName}. No period-over-period comparison is shown: this report can be asked for a range but not for a shifted window, so a prior period would have to be invented. Each finding above is measured against this location’s own rate instead.
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("reports.appointments.lifecycleFunnel", "Lifecycle funnel")}</div>
                {data.funnel.length
                  ? data.funnel.map((f) => <Bar key={f.stage} label={f.stage} value={f.count} max={funnelMax} accent={T.p1} />)
                  : <span style={{ fontSize: 12, color: T.soft }}>No lifecycle stages returned for this window.</span>}
                <span style={METHOD}>Stages as the report returns them; the largest fall between two of them is finding 2.</span>
              </Card>
              <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("reports.appointments.howItWasBooked", "How it was booked")}</div>
                {data.byOrigin.length
                  ? data.byOrigin.map((o) => <Bar key={o.origin} label={ORIGIN_LABEL[o.origin] ?? o.origin} value={o.count} max={originMax} accent={T.blue1} />)
                  : <span style={{ fontSize: 12, color: T.soft }}>No booking origins recorded for this window.</span>}
                <span style={METHOD}>Where the booking was made, not who delivered it. Staff-booked, concierge and day book are all staff-led.</span>
              </Card>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Card style={{ padding: 0 }}>
                <div style={{ padding: "14px 18px 10px", fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("reports.appointments.byService", "By service")}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr>
                    <th style={TH}>{t("reports.appointments.service", "Service")}</th>
                    <th style={{ ...TH, textAlign: "right" }}>{t("reports.appointments.booked", "Booked")}</th>
                    <th style={{ ...TH, textAlign: "right" }}>Kept</th>
                    <th style={{ ...TH, textAlign: "right" }}>No-shows</th>
                    <th style={{ ...TH, textAlign: "right" }}>No-show %</th>
                  </tr></thead>
                  <tbody>
                    {svcRows.length === 0 ? (
                      <tr><td colSpan={5} style={{ ...TD, textAlign: "center", padding: 26, color: T.soft }}>No services in this view.</td></tr>
                    ) : svcRows.map((s) => {
                      const r = s.booked ? s.noShow / s.booked : 0;
                      const house = k.booked ? k.noShow / k.booked : 0;
                      const thin = s.booked < MIN_BOOKED;
                      return (
                        <tr key={s.id} style={{ borderTop: `1px solid ${T.n6}` }}>
                          <td style={{ ...TD, fontWeight: 600, color: T.n1 }}>
                            {s.name}
                            <div style={{ height: 4, background: T.n6, borderRadius: 999, marginTop: 5, overflow: "hidden", maxWidth: 180 }}><div style={{ height: "100%", width: `${(s.booked / svcMax) * 100}%`, background: T.p1 }} /></div>
                          </td>
                          <td style={{ ...TD, ...MONO, textAlign: "right", fontWeight: 700, color: T.n1 }}>{s.booked}</td>
                          <td style={{ ...TD, ...MONO, textAlign: "right", color: T.green1 }}>{s.kept}</td>
                          <td style={{ ...TD, ...MONO, textAlign: "right", color: T.orange1 }}>{s.noShow}</td>
                          <td style={{ ...TD, ...MONO, textAlign: "right", fontWeight: 600, color: thin ? T.soft : r > house * 1.5 ? T.red1 : T.n2 }}
                            title={thin ? `Under ${MIN_BOOKED} bookings — too few to read as a rate` : `Location rate ${Math.round(house * 100)}%`}>
                            {thin ? "—" : `${Math.round(r * 100)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ padding: "9px 18px", borderTop: `1px solid ${T.n6}` }}>
                  <span style={METHOD}>No-show ÷ booked per service. A dash means fewer than {MIN_BOOKED} bookings; red means more than half again the location’s {Math.round((k.booked ? k.noShow / k.booked : 0) * 100)}%.</span>
                </div>
              </Card>

              <Card style={{ padding: 0 }}>
                <div style={{ padding: "14px 18px 10px", fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("reports.appointments.byStaff", "By staff")}</div>
                {staffNamesVisible ? (
                  <>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead><tr>
                        <th style={TH}>{t("reports.appointments.staffMember", "Staff member")}</th>
                        <th style={{ ...TH, textAlign: "right" }}>{t("reports.appointments.booked", "Booked")}</th>
                        <th style={{ ...TH, textAlign: "right" }}>Kept</th>
                        <th style={{ ...TH, textAlign: "right" }}>No-shows</th>
                        <th style={{ ...TH, textAlign: "right" }}>Kept %</th>
                      </tr></thead>
                      <tbody>
                        {staffRows.length === 0 ? (
                          <tr><td colSpan={5} style={{ ...TD, textAlign: "center", padding: 26, color: T.soft }}>No staff in this view.</td></tr>
                        ) : staffRows.map((s) => {
                          const thin = s.booked < MIN_BOOKED;
                          return (
                            <tr key={s.id} style={{ borderTop: `1px solid ${T.n6}` }}>
                              <td style={{ ...TD, fontWeight: 600, color: T.n1 }}>{s.name}</td>
                              <td style={{ ...TD, ...MONO, textAlign: "right", fontWeight: 700, color: T.n1 }}>{s.booked}</td>
                              <td style={{ ...TD, ...MONO, textAlign: "right", color: T.green1 }}>{s.kept}</td>
                              <td style={{ ...TD, ...MONO, textAlign: "right", color: T.orange1 }}>{s.noShow}</td>
                              <td style={{ ...TD, ...MONO, textAlign: "right", fontWeight: 600, color: thin ? T.soft : T.n2 }}
                                title={thin ? `Under ${MIN_BOOKED} bookings — too few to read as a rate` : undefined}>
                                {thin ? "—" : `${Math.round((s.kept / s.booked) * 100)}%`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ padding: "9px 18px", borderTop: `1px solid ${T.n6}` }}>
                      <span style={METHOD}>Kept ÷ booked per staff member. Not adjusted for time of day, service or lead time — see the note above the tiles.</span>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>Staff comparisons sit behind a scope your role does not hold</span>
                    <span style={{ fontSize: 12, lineHeight: 1.5, color: T.n2, textWrap: "pretty" }}>
                      Everything else on this page reads normally. Named per-staff kept rates need the advisor-roster capability, because a kept rate attached to a person reads as a performance rating whatever the framing says.
                    </span>
                  </div>
                )}
              </Card>
            </div>

            <span style={METHOD}>
              A service or staff row cannot yet open the appointments behind it: the appointments read is day-scoped, with no service, outcome or range filter. The gated export above carries these rows in the meantime.
            </span>
          </>
        )}
        <DataSourceStrip screen="reports-appointments" ops={["getAppointmentsReport"]} reqId="REQ-089" />
      </div>

      <ConfirmModal open={!!confirmVerb} title={confirmVerb && confirmVerb.verb ? confirmVerb.verb.label : "Route this"}
        confirmLabel="Record the hand-off"
        onClose={() => setConfirmVerb(null)}
        onConfirm={() => { const f = confirmVerb; setConfirmVerb(null); if (f) runVerb(f); }}
        message={confirmVerb ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: T.n2 }}>
            <span style={{ color: T.n1, fontWeight: 600 }}>{confirmVerb.action}</span>
            <span>Owner — {confirmVerb.owner}</span>
            <span>Nothing is sent to a student: there is no service-level reminder op yet, so this records the hand-off against the finding in the audit trail with you as the actor.</span>
            <span style={METHOD}>{confirmVerb.method}</span>
          </span>
        ) : null} />

      <ConfirmModal open={exportOpen} title="Export this report" confirmLabel="Export and log"
        onClose={() => setExportOpen(false)} onConfirm={doExport}
        message={data && k ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: T.n2 }}>
            <span><strong style={{ color: T.n1 }}>{svcRows.length} service rows{staffNamesVisible ? ` and ${staffRows.length} staff rows` : ""}</strong> — booked, kept, no-shows and both rates, stamped with the window, your name and the time.</span>
            {staffNamesVisible ? <span>Staff are named in this file. It is workload evidence; the audit trail records that you took it.</span> : <span>Staff rows are excluded — your role does not hold the staff-comparison scope.</span>}
            <span style={METHOD}>{rangeLabel} · {locName}{active ? ` · finding: ${active.value} ${active.headline}` : ""}</span>
          </span>
        ) : null} />
    </Screen>
  );
}
